import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getShowsByCategory } from '@/mocks/shows';
import NotFound from '@/pages/NotFound';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryShows = getShowsByCategory(activeSlug);

  const sortedShows = [...categoryShows].sort((a, b) => {
    if (sortBy === 'hot') return b.rating - a.rating;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  if (!currentCategory) {
    return <NotFound />;
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 天天影视`,
    description: currentCategory.description,
    url: `https://tiantian.tv/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '天天影视',
      url: 'https://tiantian.tv',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />

        <main className="flex-1">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '综艺分类', href: '/category/all' },
              { label: currentCategory.name },
            ]}
          />

          <section className="w-full px-4 md:px-6 pb-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {currentCategory.name}
                </h1>
                <p className="text-sm md:text-base text-foreground-500">{currentCategory.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-all cursor-pointer ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-white font-medium'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer ${
                      sortBy === 'latest'
                        ? 'bg-secondary-100 text-secondary-700 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className="ri-time-line mr-1"></i>最新
                  </button>
                  <button
                    onClick={() => setSortBy('hot')}
                    className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer ${
                      sortBy === 'hot'
                        ? 'bg-secondary-100 text-secondary-700 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className="ri-fire-line mr-1"></i>最热
                  </button>
                </div>
              </div>

              {sortedShows.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {sortedShows.map((show) => (
                    <ShowCard key={show.id} show={show} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无节目，请浏览其他分类</p>
                  <Link
                    to="/category/all"
                    className="mt-4 inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                  >
                    浏览全部综艺 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}